import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AvailabilityStatus,
  CategorySlug,
  ProviderVerificationLevel,
} from "@mestergo/shared";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLocale } from "../lib/i18n";
import { api } from "../lib/api";
import { getTelegramWebApp } from "../lib/telegram";
import { useAuthStore } from "../stores/auth.store";

interface HandymanProfileResponse {
  user: {
    displayName?: string | null;
    phone?: string | null;
    avatarUrl?: string | null;
  };
  headline?: string | null;
  bio?: string | null;
  yearsExperience?: number | null;
  city?: string | null;
  serviceRadiusKm?: number | null;
  availabilityStatus: AvailabilityStatus;
  verificationLevel: ProviderVerificationLevel;
  categories: Array<{ slug: CategorySlug }>;
  zones: Array<{ city: string; areaName?: string | null; radiusKm?: number | null }>;
}


export function HandymanProfilePage() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const { fetchUser } = useAuthStore();
  const [profile, setProfile] = useState<HandymanProfileResponse | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [headline, setHeadline] = useState("");
  const [bio, setBio] = useState("");
  const [yearsExperience, setYearsExperience] = useState("0");
  const [city, setCity] = useState("Chișinău");
  const [serviceRadiusKm, setServiceRadiusKm] = useState("15");
  const [availabilityStatus, setAvailabilityStatus] = useState<AvailabilityStatus>(AvailabilityStatus.Available);
  const [selectedCategories, setSelectedCategories] = useState<CategorySlug[]>([]);
  const [zoneCity, setZoneCity] = useState("Chișinău");
  const [zoneArea, setZoneArea] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get<HandymanProfileResponse>("/handymen/me").then(({ data }) => {
      setProfile(data);
      setDisplayName(data.user.displayName ?? "");
      setPhone(data.user.phone ?? "");
      setAvatarUrl(data.user.avatarUrl ?? "");
      setHeadline(data.headline ?? "");
      setBio(data.bio ?? "");
      setYearsExperience(String(data.yearsExperience ?? 0));
      setCity(data.city ?? "Chișinău");
      setServiceRadiusKm(String(data.serviceRadiusKm ?? 15));
      setAvailabilityStatus(data.availabilityStatus);
      setSelectedCategories(data.categories.map((item) => item.slug));
      setZoneCity(data.zones[0]?.city ?? "Chișinău");
      setZoneArea(data.zones[0]?.areaName ?? "");
      void fetchUser();
    });
  }, [fetchUser]);

  useEffect(() => {
    const tg = getTelegramWebApp();
    if (!tg) return;
    tg.BackButton.show();
    const onBack = () => navigate("/");
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton.offClick(onBack);
      tg.BackButton.hide();
    };
  }, [navigate]);

  const categoryOptions: Array<{ slug: CategorySlug; label: string; icon: string }> = [
    { slug: CategorySlug.Plumber, label: t.categories.plumber, icon: "🪠" },
    { slug: CategorySlug.Electrician, label: t.categories.electrician, icon: "⚡" },
    { slug: CategorySlug.ApplianceRepair, label: t.categories.applianceRepair, icon: "🔧" },
    { slug: CategorySlug.Cleaning, label: t.categories.cleaning, icon: "🧹" },
    { slug: CategorySlug.Moving, label: t.categories.moving, icon: "📦" },
    { slug: CategorySlug.Taxi, label: t.categories.taxi, icon: "🚕" },
    { slug: CategorySlug.Other, label: t.categories.other, icon: "🛠" },
  ];

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    await api.put("/handymen/me", {
      displayName,
      phone,
      avatarUrl,
      headline,
      bio,
      yearsExperience: Number(yearsExperience),
      city,
      serviceRadiusKm: Number(serviceRadiusKm),
      availabilityStatus,
    });
    await api.put("/handymen/me/categories", { categorySlugs: selectedCategories });
    await api.put("/handymen/me/zones", {
      zones: [{ city: zoneCity, areaName: zoneArea, radiusKm: Number(serviceRadiusKm) }],
    });
    setSaved(true);
  };

  if (!profile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-sm text-gray-400">{t.loading}</div>
      </div>
    );
  }

  const verificationKey =
    profile.verificationLevel === ProviderVerificationLevel.IdentityVerified ? "identityVerified"
    : profile.verificationLevel === ProviderVerificationLevel.ProVerified ? "proVerified"
    : profile.verificationLevel === ProviderVerificationLevel.TrustedPro ? "trustedPro"
    : "unverified";

  const verificationColor =
    verificationKey === "unverified" ? "bg-gray-100 text-gray-500"
    : verificationKey === "trustedPro" ? "bg-emerald-100 text-emerald-800"
    : "bg-orange-50 text-orange-600";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-100 bg-white px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <button type="button" onClick={() => navigate("/")} className="text-sm font-semibold text-gray-500">
            {t.back}
          </button>
          <LanguageSwitcher />
        </div>
        <div className="text-2xl font-bold text-gray-900">{t.handymanProfileTitle}</div>
        <div className="mt-1 text-sm text-gray-500">{t.handymanProfileSubtitle}</div>
      </div>

      <form className="space-y-4 p-5 pb-8" onSubmit={handleSave}>
        {/* Basic info */}
        <div className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Profil public</div>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={t.displayName}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t.phone} type="tel"
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />
          <input value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} placeholder={t.avatarUrl}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />
          <input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder={t.headline}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder={t.bio} rows={3}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400 resize-none" />
        </div>

        {/* Work details */}
        <div className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Detalii lucru</div>
          <div className="grid grid-cols-2 gap-3">
            <input value={yearsExperience} onChange={(e) => setYearsExperience(e.target.value)} placeholder={t.yearsExperience} type="number" min="0"
              className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />
            <input value={serviceRadiusKm} onChange={(e) => setServiceRadiusKm(e.target.value)} placeholder={t.serviceRadius} type="number" min="0"
              className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />
          </div>
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t.city}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />

          {/* Availability */}
          <div>
            <div className="mb-2 text-sm font-semibold text-gray-700">{t.availability}</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: AvailabilityStatus.Available, label: t.availabilityLabels.available, icon: "🟢" },
                { value: AvailabilityStatus.Busy, label: t.availabilityLabels.busy, icon: "🟡" },
                { value: AvailabilityStatus.Offline, label: t.availabilityLabels.offline, icon: "⚫" },
                { value: AvailabilityStatus.OnVacation, label: t.availabilityLabels.onVacation, icon: "🏖" },
              ].map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setAvailabilityStatus(item.value)}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold text-left transition-colors ${
                    availabilityStatus === item.value ? "border-orange-500 bg-orange-50 text-orange-600" : "border-gray-200 text-gray-600"
                  }`}
                >
                  {item.icon} {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-400">{t.category}</div>
          <div className="grid grid-cols-2 gap-2">
            {categoryOptions.map((item) => {
              const active = selectedCategories.includes(item.slug);
              return (
                <button
                  key={item.slug}
                  type="button"
                  onClick={() => setSelectedCategories((prev) =>
                    prev.includes(item.slug) ? prev.filter((v) => v !== item.slug) : [...prev, item.slug]
                  )}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold text-left transition-colors ${
                    active ? "border-orange-500 bg-orange-50 text-orange-600" : "border-gray-200 text-gray-600"
                  }`}
                >
                  {item.icon} {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Zones */}
        <div className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">{t.zones}</div>
          <input value={zoneCity} onChange={(e) => setZoneCity(e.target.value)} placeholder={t.city}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />
          <input value={zoneArea} onChange={(e) => setZoneArea(e.target.value)} placeholder={t.zones}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />
        </div>

        {/* Verification status */}
        <div className="rounded-2xl bg-white px-5 py-4 shadow-sm flex items-center justify-between">
          <div className="text-sm text-gray-500">{t.verificationLevel}</div>
          <div className={`rounded-full px-3 py-1 text-xs font-semibold ${verificationColor}`}>
            {t.verificationLabels[verificationKey as keyof typeof t.verificationLabels]}
          </div>
        </div>

        <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          {t.profilePublicNote}
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => navigate("/handyman/portfolio")}
            className="rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm font-semibold text-gray-700 active:bg-gray-50 transition-colors"
          >
            📸 {t.managePortfolio}
          </button>
          <button
            type="button"
            onClick={() => navigate("/handyman/verification")}
            className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3.5 text-sm font-semibold text-orange-600 active:bg-orange-100 transition-colors"
          >
            ✓ {t.manageVerification}
          </button>
        </div>

        {saved ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {t.profileSaved}
          </div>
        ) : null}

        <button type="submit" className="w-full rounded-xl bg-orange-500 px-4 py-4 text-sm font-semibold text-white active:bg-orange-600 transition-colors">
          {t.save}
        </button>
      </form>
    </div>
  );
}
